import { useLocation } from 'react-router';

import { FALLBACK_LANGUAGE, type Language, LANGUAGES } from '@/modules/i18n/languages';
import { useCommonTranslation } from '@/modules/i18n/useTranslation';
import { buildLanguagePath, parseLanguagePath } from '@/modules/routing';

interface PageMetaProps {
    title: string;
    // Canonical and hreflang links to the page in every language; off for a page that has no twins (404)
    alternates?: boolean;
}

// React 19 hoists these tags into <head> but doesn't deduplicate them: render one PageMeta per page.
// Site-wide meta tags are static in index.html.
const PageMeta = ({ title, alternates = true }: PageMetaProps) => {
    const { t } = useCommonTranslation();
    const { pathname } = useLocation();
    const { lang, rest } = parseLanguagePath(pathname);
    // The page path alone: no query string reaches search engines
    const urlIn = (language: Language) => `${window.location.origin}${buildLanguagePath(language, rest)}`;

    return (
        <>
            <title>{`${title} ${t('seo.titlePostfix')}`}</title>
            <meta name="twitter:title" content={title}/>
            <meta property="og:title" content={title}/>
            {alternates && lang !== undefined && (
                <>
                    <link rel="canonical" href={urlIn(lang)}/>
                    {LANGUAGES.map((language) => (
                        <link key={language} rel="alternate" hrefLang={language} href={urlIn(language)}/>
                    ))}
                    <link rel="alternate" hrefLang="x-default" href={urlIn(FALLBACK_LANGUAGE)}/>
                </>
            )}
        </>
    );
};

export default PageMeta;
